import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import GenericTable from "@components/tables/GenericTable";
import CreateProject from "@components/modals/CreateProject";
import CreateFolder from "@components/modals/CreateFolder";
import FileUpload from "@components/modals/FileUpload";
import {
  deleteProject,
  fetchAllProjects,
} from "@store/thunks/dashboardThunk";
import {
  setSelectedProjectId
} from "@store/slices/dashboardSlice";
import { openModal } from "@store/slices/modalSlice";
import { restrictSpecialCharacters } from "@/utils/constants";

const GAProjectTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { projects } = useSelector((store) => store.dashboard);
  const [searchQuery, setSearchQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true); // 👈 track if more pages exist
  const [popup, setPopup] = useState({ isOpen: false, type: "", message: "" });

 const handleInputChange = (e) => {
    const newValue = e.target.value;
    if (restrictSpecialCharacters(newValue) || newValue === "") {
      setInputValue(newValue);
      setSearchQuery(newValue);
      setPageIndex(0);
      setHasNextPage(true); // Reset search → assume more pages exist
    } else {
      setPopup({
        isOpen: true,
        type: "error",
        message: "Special characters are not allowed. Use only letters, numbers, and spaces.",
      });
    }
  };

 const handlePopupClose = () => {
    setPopup({ isOpen: false, type: "", message: "" });
  };


  useEffect(() => {
    const fetchData = async () => {
      const res = await dispatch(fetchAllProjects("GA", searchQuery, pageIndex, pageSize));
      if (Array.isArray(res?.data)) {
        if (res.data.length < pageSize) {
          setHasNextPage(false);
        } else {
          setHasNextPage(true);
        }
      }
    };
    fetchData();
  }, [dispatch, searchQuery, pageIndex, pageSize]);


  const handleProjectClick = ({ row }) => {
    dispatch(setSelectedProjectId(row.original.projectId));
    navigate("/superAdmin/ga/folders");
  };

  const handleDelete = ({ row }) => {
    if (confirm(`You want to delete project: ${row.original.projectName}`)) {
      dispatch(deleteProject("GA", row.original.projectId));
    }
  };

  const handleOpenCreateProject = () => {
    dispatch(openModal("createProject"));
  };

  const handleClickBack = () => {
    navigate(-1);
  };

  const handleClickHome = () => {
    navigate("/superAdmin");
  };

  const handleApprovedFiles = () => {
    navigate("/superAdmin/ga/approved-projects");
  };


  const projectColumns = [
    {
      id: "serial_number",
      header: () => "Sr. No.",
      cell: ({ row, table }) => {
        return pageIndex * pageSize + row.index + 1;
      },
      cellClassName: "text-center",
    },
    {
      id: "projectName",
      header: () => "Project Name",
      accessorKey: "projectName",
      cell: ({ row }) => (
        <button
          onClick={() => handleProjectClick({ row })}
          className="text-[#ec7026] hover:text-[#d85d1a] focus:underline"
        >
          {row.original.projectName}
        </button>
      ),
    },
    {
      id: "description",
      header: () => "Description",
      accessorKey: "description",
      cell: ({ row }) => (
        <div className="max-w-md">
          {row.original.description || "No description available"}
        </div>
      ),
    },
    {
      header: "Created At",
      accessorKey: "createdAt",
      cellClassName: "cursor-pointer hover:text-blue-600 text-center",
      cell: ({ row }) => {
        const deadline = row.original?.createdAt || row.createdAt;
        if (!deadline) return "N/A";
        try {
          const date = new Date(deadline);
          const now = new Date();
          const isOverdue = date < now;
          const isApproaching = !isOverdue && date - now < 7 * 24 * 60 * 60 * 1000;
          return (
            <div>
              <div
                className={
                  isOverdue
                    ? "text-red-600"
                    : isApproaching
                      ? "text-amber-600"
                      : null
                }
              >
                {date.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>
              <div className="text-sm text-gray-500">
                {date.toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                })}
              </div>
            </div>
          );
        } catch (e) {
          return deadline;
        }
      },
    },
    {
      header: "Deadline",
      accessorKey: "deadline",
      cellClassName: "cursor-pointer hover:text-blue-600 text-center",
      cell: ({ row }) => {
        const deadline = row.original?.deadline || row.deadline;
        if (!deadline) return "N/A";
        try {
          const date = new Date(deadline);
          const now = new Date();
          const isOverdue = date < now;
          const isApproaching = !isOverdue && date - now < 7 * 24 * 60 * 60 * 1000;
          return (
            <div>
              <div
                className={
                  isOverdue
                    ? "text-red-600"
                    : isApproaching
                      ? "text-amber-600"
                      : null
                }
              >
                {date.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>
              <div className="text-sm text-gray-500">
                {date.toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                })}
              </div>
            </div>
          );
        } catch (e) {
          return deadline;
        }
      },
    },
    {
      id: "delete",
      header: () => "Delete",
      accessorKey: "delete",
      cell: ({ row }) => (
        <button
          onClick={() => handleDelete({ row })}
          className="inline-flex items-center px-3 py-1.5 border border-red-500 text-red-500 hover:bg-red-50 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 rounded-lg"
        >
          <svg
            className="w-4 h-4 mr-1.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
          Delete
        </button>
      ),
      cellClassName: "text-center",
    },
  ];

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)] bg-gray-50/50 mt-14">
      <CreateProject />
      <CreateFolder />
      <FileUpload />
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
            <div className="flex flex-col">
              <h1 className="text-2xl font-['Semantic/Strong'] font-bold text-gray-900">
                <span className="text-[#ec7026] shadow-sm">GA Solutions</span>{" "}
                Projects
              </h1>
              <p className="text-sm text-gray-500 font-['Semantic/Strong']">
                Manage and oversee GA Solutions projects
              </p>
            </div>

            <div className="flex flex-wrap sm:flex-nowrap gap-2 items-center">
              <div className="relative w-full sm:w-64">
                <input
                  type="text"
                  placeholder="Search projects..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] text-base font-['Semantic/Strong'] text-gray-700 pl-10"
                  value={inputValue}
                  onChange={handleInputChange}
                />
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>

              {popup.isOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-md w-full mx-4">
              <h2 className="text-lg font-['Semantic/Strong'] font-bold text-gray-900">
                {popup.type === "error" ? "Error" : "Info"}
              </h2>
              <p className="mt-2 text-sm text-gray-500 font-['Semantic/Strong']">
                {popup.message}
              </p>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={handlePopupClose}
                  className="inline-flex items-center px-4 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026]"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

              <button
                onClick={handleClickHome}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
              >
                <svg
                  className="w-4 h-4 mr-1.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                <span>Home</span>
              </button>

              <button
                onClick={handleClickBack}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
              >
                <svg
                  className="w-4 h-4 mr-1.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                <span>Back</span>
              </button>

              <button
                onClick={handleApprovedFiles}
                className="inline-flex items-center px-3 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
              >
                <svg
                  className="w-4 h-4 mr-1.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>Approved Projects</span>
              </button>

              <button
                onClick={handleOpenCreateProject}
                className="inline-flex items-center px-3 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
              >
                <svg
                  className="w-4 h-4 mr-1.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
                <span>Create Project</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full p-4 sm:p-6 lg:p-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="w-full overflow-x-auto">
            <GenericTable
              data={projects}
              columns={projectColumns}
              showPagination={true}
              manualPagination={true}
              pageIndex={pageIndex} 
              pageSize={pageSize}   
              hasNextPage={hasNextPage}
              onPaginationChange={({ pageIndex, pageSize }) => {
                setPageIndex(pageIndex);
                setPageSize(pageSize);
              }}
              emptyMessage={searchQuery ? "No projects match your search" : "No GA projects available"}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GAProjectTable;